import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import { NavigationBar } from '../components/NavigationBar';
import { useTheme } from '../context/ThemeContext'; // Import useTheme
import { UsersIcon, LightBulbIcon, AcademicCapIcon } from '@heroicons/react/24/outline'; // Example icons

export const About = () => {
  const { darkMode } = useTheme(); // Get darkMode state

  const [missionRef, missionInView] = useInView({
    triggerOnce: true,
    threshold: 0.2,
  });

  const [valuesRef, valuesInView] = useInView({
    triggerOnce: true,
    threshold: 0.2,
  });

  const [teamRef, teamInView] = useInView({
    triggerOnce: true,
    threshold: 0.2,
  });

  const fadeInUp = {
    hidden: { opacity: 0, y: 30 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.6, ease: "easeOut" } }
  };

  const values = [
    {
      name: 'Innovation',
      description: 'We constantly explore new technologies to enhance the learning experience.',
      icon: LightBulbIcon,
    },
    {
      name: 'Accessibility',
      description: 'Making quality education tools available to everyone, everywhere.',
      icon: UsersIcon,
    },
    {
      name: 'Effectiveness',
      description: 'Focusing on results and helping learners achieve their academic goals.',
      icon: AcademicCapIcon,
    },
  ];

  return (
    <div className={`min-h-screen ${darkMode ? 'dark' : ''} bg-gradient-to-b from-gray-50 to-white dark:from-gray-900 dark:to-gray-800 overflow-hidden`}>
      <NavigationBar />

      {/* Hero Section - Enhanced */}
      <section className="relative pt-24 pb-20 text-center lg:pt-36 overflow-hidden">
        {/* Background Gradient & Shapes */}
        <div className="absolute inset-0 -z-10">
          <div className="absolute inset-0 bg-[radial-gradient(40%_50%_at_50%_30%,theme(colors.purple.100),transparent)] dark:bg-[radial-gradient(40%_50%_at_50%_30%,theme(colors.purple.900/40),transparent)]" />
          <div className="absolute inset-x-0 -top-40 -z-10 transform-gpu overflow-hidden blur-3xl sm:-top-80" aria-hidden="true">
            <div
              className="relative left-[calc(50%-11rem)] aspect-[1155/678] w-[36.125rem] -translate-x-1/2 rotate-[30deg] bg-gradient-to-tr from-[#ff80b5] to-[#9089fc] opacity-20 sm:left-[calc(50%-30rem)] sm:w-[72.1875rem]"
              style={{
                clipPath:
                  'polygon(74.1% 44.1%, 100% 61.6%, 97.5% 26.9%, 85.5% 0.1%, 80.7% 2%, 72.5% 32.5%, 60.2% 62.4%, 52.4% 68.1%, 47.5% 58.3%, 45.2% 34.5%, 27.5% 76.7%, 0.1% 64.9%, 17.9% 100%, 27.6% 76.8%, 76.1% 97.7%, 74.1% 44.1%)',
              }}
            />
          </div>
        </div>

        <motion.div
          className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 relative z-10"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
        >
          <motion.h1
            className="text-4xl font-bold tracking-tight text-gray-900 dark:text-white sm:text-6xl"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2, duration: 0.6 }}
          >
            Revolutionizing Learning Through Technology
          </motion.h1>
          <motion.p
            className="mx-auto mt-6 max-w-2xl text-lg text-gray-600 dark:text-gray-300"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.4, duration: 0.6 }}
          >
            We're on a mission to make learning more engaging, effective, and accessible for everyone, powered by intelligent tools.
          </motion.p>
        </motion.div>
      </section>

      {/* Mission Section - Enhanced */}
      <motion.section
        ref={missionRef}
        className="relative py-20 bg-white dark:bg-gray-800/50 backdrop-blur-lg overflow-hidden"
        initial={{ opacity: 0 }}
        animate={missionInView ? { opacity: 1 } : { opacity: 0 }}
        transition={{ duration: 0.8 }}
      >
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 gap-16 lg:grid-cols-2 lg:gap-12 items-center">
            {/* Image with enhanced styling */}
            <motion.div
              className="relative group aspect-w-4 aspect-h-3 lg:aspect-w-5 lg:aspect-h-4"
              initial={{ opacity: 0, x: -50 }}
              animate={missionInView ? { opacity: 1, x: 0 } : { opacity: 0, x: -50 }}
              transition={{ duration: 0.7, delay: 0.2 }}
            >
              <div className="absolute inset-0 bg-gradient-to-br from-indigo-500 to-purple-600 rounded-3xl transform rotate-[-3deg] group-hover:rotate-[-1deg] group-hover:scale-105 transition-transform duration-500 ease-out shadow-2xl"></div>
              <div className="relative h-full w-full rounded-3xl shadow-xl overflow-hidden transform rotate-[2deg] group-hover:rotate-[1deg] group-hover:scale-105 transition-transform duration-500 ease-out border-4 border-white dark:border-gray-700">
                <img
                  src="https://images.unsplash.com/photo-1522202176988-66273c2fd55f?ixlib=rb-4.0.3&ixid=MnwxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8&auto=format&fit=crop&w=1471&q=80"
                  alt="Team collaboration on learning platform"
                  className="h-full w-full object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/30 to-transparent"></div>
              </div>
            </motion.div>

            {/* Text content */}
            <motion.div
              className="flex flex-col justify-center"
              initial={{ opacity: 0, x: 50 }}
              animate={missionInView ? { opacity: 1, x: 0 } : { opacity: 0, x: 50 }}
              transition={{ duration: 0.7, delay: 0.3 }}
            >
              <h2 className="text-base font-semibold leading-7 text-indigo-600 dark:text-indigo-400">Our Mission</h2>
              <p className="mt-2 text-3xl font-bold tracking-tight text-gray-900 dark:text-white sm:text-4xl">Empowering Learners</p>
              <p className="mt-6 text-lg leading-8 text-gray-600 dark:text-gray-300">
                At PowerQuizzer, we believe learning should be an engaging and personalized journey.
                Our platform combines cutting-edge AI with proven methodologies to create
                an adaptive experience that evolves with each user, unlocking their full potential.
              </p>
              {/* Stats with improved styling */}
              <dl className="mt-10 grid grid-cols-2 gap-x-6 gap-y-8 text-center lg:text-left">
                <div className="flex flex-col-reverse gap-y-1">
                  <dt className="text-base leading-7 text-gray-600 dark:text-gray-400">Active Users</dt>
                  <dd className="text-3xl font-semibold tracking-tight text-gray-900 dark:text-white">1M+</dd>
                </div>
                <div className="flex flex-col-reverse gap-y-1">
                  <dt className="text-base leading-7 text-gray-600 dark:text-gray-400">Quizzes Created</dt>
                  <dd className="text-3xl font-semibold tracking-tight text-gray-900 dark:text-white">5M+</dd>
                </div>
              </dl>
            </motion.div>
          </div>
        </div>
      </motion.section>

      {/* Our Values Section - New Section */}
      <motion.section
        ref={valuesRef}
        className="relative py-20 bg-gradient-to-b from-gray-50 to-white dark:from-gray-900 dark:to-gray-800"
        initial="hidden"
        animate={valuesInView ? "visible" : "hidden"}
        variants={{ visible: { transition: { staggerChildren: 0.15 } } }}
      >
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <motion.div className="text-center" variants={fadeInUp}>
            <h2 className="text-base font-semibold leading-7 text-indigo-600 dark:text-indigo-400">Our Core Values</h2>
            <p className="mt-2 text-3xl font-bold tracking-tight text-gray-900 dark:text-white sm:text-4xl">
              Guiding Principles
            </p>
            <p className="mt-6 text-lg leading-8 text-gray-600 dark:text-gray-300 max-w-2xl mx-auto">
              These values shape our culture, drive our decisions, and define our commitment to learners.
            </p>
          </motion.div>

          <div className="mx-auto mt-16 max-w-2xl lg:mt-20 lg:max-w-none">
            <dl className="grid max-w-xl grid-cols-1 gap-x-8 gap-y-16 lg:max-w-none lg:grid-cols-3">
              {values.map((value) => (
                <motion.div key={value.name} className="flex flex-col items-center text-center p-6 bg-white dark:bg-gray-800/60 rounded-xl shadow-lg border border-gray-100 dark:border-gray-700/50 transition-shadow hover:shadow-indigo-100/50 dark:hover:shadow-indigo-900/30" variants={fadeInUp}>
                  <div className="flex h-12 w-12 items-center justify-center rounded-full bg-gradient-to-r from-indigo-500 to-purple-600 mb-6 text-white shadow-md">
                    <value.icon className="h-6 w-6" aria-hidden="true" />
                  </div>
                  <dt className="text-xl font-semibold leading-7 text-gray-900 dark:text-white">{value.name}</dt>
                  <dd className="mt-2 flex-auto text-base leading-7 text-gray-600 dark:text-gray-300">{value.description}</dd>
                </motion.div>
              ))}
            </dl>
          </div>
        </div>
      </motion.section>

      {/* Team Section - Simplified & Centered */}
      <motion.section
        ref={teamRef}
        className="relative py-20 bg-white dark:bg-gray-800/50 backdrop-blur-lg"
        initial={{ opacity: 0 }}
        animate={teamInView ? { opacity: 1 } : { opacity: 0 }}
        transition={{ duration: 0.8 }}
      >
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <motion.div
            className="text-center"
            initial={{ opacity: 0, y: 20 }}
            animate={teamInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 20 }}
            transition={{ duration: 0.6 }}
          >
            <h2 className="text-3xl font-bold tracking-tight text-gray-900 dark:text-white">Meet Our Founder</h2>
            <p className="mt-4 text-lg text-gray-600 dark:text-gray-300 max-w-xl mx-auto">
              The driving force behind PowerQuizzer's mission to transform education.
            </p>
          </motion.div>
          <div className="mt-16 flex justify-center">
            {teamMembers.map((member, index) => (
              <motion.div
                key={member.name}
                className="relative group w-full max-w-xs bg-white dark:bg-gray-800 rounded-2xl shadow-xl p-8 border border-gray-100 dark:border-gray-700/50 overflow-hidden"
                initial={{ opacity: 0, scale: 0.9 }}
                animate={teamInView ? { opacity: 1, scale: 1 } : { opacity: 0, scale: 0.9 }}
                transition={{ delay: index * 0.1 + 0.2, duration: 0.6, ease: "easeOut" }}
              >
                {/* Subtle background pattern */}
                <div className="absolute inset-0 opacity-5 dark:opacity-[0.03] [mask-image:radial-gradient(100%_100%_at_top_right,white,transparent)]">
                  <svg aria-hidden="true" className="absolute inset-0 h-full w-full">
                    <defs>
                      <pattern id="about-pattern" width="80" height="80" patternUnits="userSpaceOnUse" x="50%" y="100%" patternTransform="translate(0 -1)">
                        <path d="M0 80V0h80" fill="none" stroke="currentColor" strokeWidth="1"></path>
                      </pattern>
                    </defs>
                    <rect width="100%" height="100%" fill="url(#about-pattern)"></rect>
                  </svg>
                </div>
                <div className="relative flex flex-col items-center">
                  <div className="relative h-32 w-32 rounded-full overflow-hidden shadow-lg border-4 border-indigo-200 dark:border-indigo-700/50 mb-6 transition-transform duration-300 group-hover:scale-105">
                    <img
                      src={member.image}
                      alt={member.name}
                      className="h-full w-full object-cover"
                    />
                  </div>
                  <div className="text-center">
                    <h3 className="text-xl font-semibold text-gray-900 dark:text-white">{member.name}</h3>
                    <p className="text-sm text-indigo-600 dark:text-indigo-400 font-medium">{member.role}</p>
                    {/* Optional: Add a short bio */}
                    {/* <p className="mt-3 text-sm text-gray-500 dark:text-gray-400">Passionate about leveraging AI to create impactful learning tools.</p> */}
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </motion.section>
    </div>
  );
};

// Keep teamMembers definition outside the component
const teamMembers = [
  {
    name: "PowerIsTsutsun",
    role: "Founder & CEO", // Updated role
    image: "https://areajugones.sport.es/wp-content/uploads/2024/11/solo-leveling-t2-1-1560x880.jpg.webp",
  },
];